// @ts-nocheck
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ArrowLeft, Building2, Check, X, Loader2, Upload, Save, Users, Mail,
  CreditCard, ExternalLink, RotateCw, Ban, Plus,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useDebounce } from '@/hooks/useDebounce';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { InviteAdminDialog } from '@/components/admin/InviteAdminDialog';

const SUBDOMAIN_REASONS: Record<string, string> = {
  empty: 'Enter a subdomain', length: 'Must be 3–63 characters',
  format: 'Only lowercase letters, numbers, and hyphens', reserved: 'That subdomain is reserved', taken: 'Already taken',
};
const SAVE_ERRORS: Record<string, string> = {
  not_authorized: 'You are not authorized to update this organization.',
  subdomain_taken: 'That subdomain is already taken.', subdomain_reserved: 'That subdomain is reserved.',
  subdomain_format: 'Subdomain can only contain lowercase letters, numbers, and hyphens.',
  subdomain_length: 'Subdomain must be 3–63 characters.', subdomain_empty: 'Please enter a subdomain.',
};
const MAX_LOGO_BYTES = 2 * 1024 * 1024;
const ACCEPTED = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
const ROLE_LABEL: Record<string, string> = {
  rlevents_super_admin: 'Super Admin', rlevents_tenant_admin: 'Org Admin', rlevents_tenant_staff: 'Staff',
  owner: 'Owner', admin: 'Admin', org_admin: 'Org Admin',
};

export default function AdminOrganizationDetail() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [subdomain, setSubdomain] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [checkState, setCheckState] = useState<{ status: 'idle' | 'checking' | 'available' | 'error'; message?: string }>({ status: 'idle' });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [stripeBusy, setStripeBusy] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);

  const debouncedSubdomain = useDebounce(subdomain, 450);

  const { data: org, isLoading: orgLoading, refetch: refetchOrg } = useQuery({
    queryKey: ['platform-org', orgId],
    queryFn: async () => {
      const { data, error } = await supabase.from('organizations').select('*').eq('id', orgId).single();
      if (error) throw error;
      return data;
    },
    enabled: !!orgId,
  });

  useEffect(() => {
    if (org) {
      setSubdomain(org.subdomain || '');
      setLogoUrl(org.logo_url || '');
    }
  }, [org]);

  const { data: members } = useQuery({
    queryKey: ['platform-org-members', orgId],
    queryFn: async () => {
      const { data: rows } = await supabase
        .from('organization_members')
        .select('id, user_id, role, status, joined_at')
        .eq('organization_id', orgId)
        .order('created_at');
      const ids = (rows || []).map((r: any) => r.user_id).filter(Boolean);
      let profiles: any[] = [];
      if (ids.length) {
        const { data: p } = await supabase
          .from('profiles').select('user_id, first_name, last_name, email').in('user_id', ids);
        profiles = p || [];
      }
      const byUser: Record<string, any> = {};
      profiles.forEach((p) => { byUser[p.user_id] = p; });
      return (rows || []).map((r: any) => {
        const p = byUser[r.user_id];
        const name = p ? [p.first_name, p.last_name].filter(Boolean).join(' ') : '';
        return { ...r, name: name || p?.email || 'Unknown user', email: p?.email || '' };
      });
    },
    enabled: !!orgId,
  });

  const { data: invitations, refetch: refetchInvites } = useQuery({
    queryKey: ['platform-org-invites', orgId],
    queryFn: async () => {
      const { data } = await supabase
        .from('admin_invitations')
        .select('id, invitee_email, invitee_name, role, status, expires_at, created_at')
        .eq('organization_id', orgId)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });
      return data || [];
    },
    enabled: !!orgId,
  });

  // live subdomain check (exclude current org)
  useEffect(() => {
    const check = async () => {
      const v = debouncedSubdomain.trim();
      if (!v) { setCheckState({ status: 'idle' }); return; }
      if (v === (org?.subdomain || '')) { setCheckState({ status: 'idle' }); return; }
      setCheckState({ status: 'checking' });
      try {
        const { data, error } = await (supabase as any).rpc('check_subdomain_available', { p_subdomain: v, p_org_id: orgId });
        if (error) throw error;
        if (data?.available) setCheckState({ status: 'available', message: 'Available' });
        else setCheckState({ status: 'error', message: SUBDOMAIN_REASONS[data?.reason] || 'Not available' });
      } catch { setCheckState({ status: 'error', message: 'Could not check availability' }); }
    };
    check();
  }, [debouncedSubdomain, orgId, org?.subdomain]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !orgId) return;
    if (!ACCEPTED.includes(file.type)) { toast.error('Please upload a PNG, JPG, SVG, or WebP image'); return; }
    if (file.size > MAX_LOGO_BYTES) { toast.error('Logo must be 2MB or smaller'); return; }
    try {
      setUploading(true);
      const ext = (file.name.split('.').pop() || 'png').toLowerCase();
      const path = `${orgId}/logo.${ext}`;
      const { error: upErr } = await supabase.storage.from('org-logos').upload(path, file, { cacheControl: '3600', upsert: true });
      if (upErr) throw upErr;
      const { data: urlData } = supabase.storage.from('org-logos').getPublicUrl(path);
      setLogoUrl(`${urlData.publicUrl}?t=${Date.now()}`);
      toast.success('Logo uploaded — remember to save');
    } catch (e: any) {
      toast.error(e.message || 'Failed to upload logo');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const subdomainEmpty = useMemo(() => subdomain.trim() === '', [subdomain]);

  const handleSaveBranding = async () => {
    if (!orgId) return;
    if (!subdomainEmpty && checkState.status === 'error') { toast.error('Please fix the subdomain before saving'); return; }
    try {
      setSaving(true);
      const { data, error } = await (supabase as any).rpc('save_org_branding', {
        p_org_id: orgId,
        p_subdomain: subdomainEmpty ? null : subdomain.trim(),
        p_logo_url: logoUrl ? logoUrl.split('?')[0] : null,
        p_is_recruitlook_branded: org?.is_recruitlook_branded ?? false,
        p_clear_subdomain: subdomainEmpty,
      });
      if (error) throw error;
      if (!data?.ok) { toast.error(SAVE_ERRORS[data?.error] || `Could not save (${data?.error || 'unknown error'})`); return; }
      setCheckState({ status: 'idle' });
      toast.success('Branding saved');
      refetchOrg();
    } catch (e: any) {
      toast.error(e.message || 'Failed to save branding');
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async (id: string) => {
    const { error } = await supabase
      .from('admin_invitations')
      .update({ status: 'revoked', revoked_at: new Date().toISOString() })
      .eq('id', id);
    if (error) { toast.error('Could not revoke invitation'); return; }
    toast.success('Invitation revoked');
    refetchInvites();
  };

  const handleResend = async (inv: any) => {
    // revoke existing, then re-issue a fresh invite
    await supabase.from('admin_invitations')
      .update({ status: 'revoked', revoked_at: new Date().toISOString() }).eq('id', inv.id);
    const { data, error } = await (supabase as any).functions.invoke('rle-invite-admin', {
      body: {
        invitee_email: inv.invitee_email, invitee_name: inv.invitee_name || null,
        role: inv.role, organization_id: orgId,
      },
    });
    if (error || !data?.ok) { toast.error('Could not resend invitation'); refetchInvites(); return; }
    if (data.data?.email_sent) toast.success(`Invite resent to ${inv.invitee_email}`);
    else toast.warning('Invite recreated but email failed to send');
    refetchInvites();
  };

  const handleStripeOnboarding = async () => {
    try {
      setStripeBusy(true);
      const { data, error } = await (supabase as any).functions.invoke('org-stripe-connect-onboarding', {
        body: { action: 'create_account', organization_id: orgId },
      });
      if (error) throw error;
      const url = data?.onboarding_url || data?.url;
      if (url) window.location.href = url;
      else toast.error('Could not start Stripe onboarding');
    } catch (e: any) {
      toast.error(e.message || 'Could not start Stripe onboarding');
    } finally {
      setStripeBusy(false);
    }
  };

  const handleStripeDashboard = async () => {
    try {
      setStripeBusy(true);
      const { data, error } = await (supabase as any).functions.invoke('org-stripe-connect-onboarding', {
        body: { action: 'create_login_link', organization_id: orgId },
      });
      if (error) throw error;
      const url = data?.url || data?.login_link?.url;
      if (url) window.open(url, '_blank');
      else toast.error('Could not open Stripe dashboard');
    } catch (e: any) {
      toast.error(e.message || 'Could not open Stripe dashboard');
    } finally {
      setStripeBusy(false);
    }
  };

  const handleStripeRefresh = async () => {
    try {
      setStripeBusy(true);
      const { error } = await (supabase as any).functions.invoke('org-stripe-connect-onboarding', {
        body: { action: 'get_account_status', organization_id: orgId },
      });
      if (error) throw error;
      await refetchOrg();
    } catch (e: any) {
      toast.error(e.message || 'Could not refresh Stripe status');
    } finally {
      setStripeBusy(false);
    }
  };

  if (orgLoading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
  }
  if (!org) {
    return <div className="p-6"><Button variant="ghost" onClick={() => navigate('/admin-dashboard/organizations')}><ArrowLeft className="h-4 w-4 mr-2" /> Back</Button><p className="mt-4 text-muted-foreground">Organization not found.</p></div>;
  }

  const stripeConnected = !!org.stripe_connect_onboarded_at || !!org.stripe_connect_charges_enabled;

  return (
    <div className="space-y-6 max-w-4xl">
      <Button variant="ghost" size="sm" onClick={() => navigate('/admin-dashboard/organizations')} className="-ml-2">
        <ArrowLeft className="h-4 w-4 mr-2" /> Organizations
      </Button>

      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="h-16 w-16 rounded-2xl bg-muted border flex items-center justify-center overflow-hidden shrink-0">
          {org.logo_url ? <img src={org.logo_url} alt={org.name} className="h-full w-full object-cover" /> : <Building2 className="h-6 w-6 text-muted-foreground" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Managing organization</p>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-heading truncate">{org.name}</h1>
            {org.is_active ? (
              <Badge className="rounded-full bg-green-100 text-green-700 hover:bg-green-100 border-0">
                <span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-green-600 inline-block" /> Active
              </Badge>
            ) : (
              <Badge variant="secondary" className="rounded-full">Inactive</Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            Organization settings · /{org.slug} · {org.subdomain ? `${org.subdomain}.recruitlookevents.com` : 'No subdomain'}
          </p>
        </div>
      </div>

      {/* Branding */}
      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="text-base">Branding</CardTitle>
          <CardDescription>Custom subdomain and logo for this organization.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Subdomain</Label>
            <div className="flex items-stretch">
              <Input value={subdomain} onChange={(e) => setSubdomain(e.target.value.toLowerCase())} placeholder="acme" className="rounded-r-none" autoCapitalize="none" spellCheck={false} />
              <span className="inline-flex items-center px-3 rounded-r-md border border-l-0 border-input bg-muted text-sm text-muted-foreground whitespace-nowrap">.recruitlookevents.com</span>
            </div>
            <div className="min-h-[20px] mt-1 text-sm">
              {checkState.status === 'checking' && <span className="flex items-center gap-1 text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking…</span>}
              {checkState.status === 'available' && <span className="flex items-center gap-1 text-green-600"><Check className="h-3.5 w-3.5" /> {checkState.message}</span>}
              {checkState.status === 'error' && <span className="flex items-center gap-1 text-destructive"><X className="h-3.5 w-3.5" /> {checkState.message}</span>}
            </div>
            <p className="text-xs text-muted-foreground">Subdomains require DNS configuration before they go live.</p>
          </div>

          <div>
            <Label>Logo</Label>
            <div className="flex items-center gap-3 mt-1">
              <div className="h-14 w-14 rounded-xl border bg-muted flex items-center justify-center overflow-hidden">
                {logoUrl ? <img src={logoUrl} alt="Logo" className="h-full w-full object-cover" /> : <Upload className="h-5 w-5 text-muted-foreground" />}
              </div>
              <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {logoUrl ? 'Change logo' : 'Upload logo'}
              </Button>
              <input ref={fileInputRef} type="file" accept={ACCEPTED.join(',')} className="hidden" onChange={handleFileSelect} />
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={handleSaveBranding} disabled={saving} className="rounded-full">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save branding
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Members */}
      <Card className="rounded-2xl">
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4" /> Members</CardTitle>
            <CardDescription>People who belong to this organization.</CardDescription>
          </div>
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => setInviteOpen(true)}>
            <Plus className="h-4 w-4 mr-1.5" /> Invite admin
          </Button>
        </CardHeader>
        <CardContent>
          {members && members.length > 0 ? (
            <div className="divide-y">
              {members.map((m: any) => (
                <div key={m.id} className="flex items-center justify-between py-3">
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{m.name}</div>
                    {m.email && <div className="text-xs text-muted-foreground truncate">{m.email}</div>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="rounded-full">{ROLE_LABEL[m.role] || m.role}</Badge>
                    {m.status === 'active'
                      ? <span className="text-xs text-green-600">Active</span>
                      : <span className="text-xs text-muted-foreground">{m.status}</span>}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-2">No members yet.</p>
          )}
        </CardContent>
      </Card>

      {/* Pending invitations */}
      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Mail className="h-4 w-4" /> Pending invitations</CardTitle>
          <CardDescription>Invites that haven't been accepted yet.</CardDescription>
        </CardHeader>
        <CardContent>
          {invitations && invitations.length > 0 ? (
            <div className="divide-y">
              {invitations.map((inv: any) => (
                <div key={inv.id} className="flex items-center justify-between py-3 gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{inv.invitee_email}</div>
                    <div className="text-xs text-muted-foreground">
                      {ROLE_LABEL[inv.role] || inv.role}
                      {inv.expires_at ? ` · expires ${new Date(inv.expires_at).toLocaleDateString()}` : ''}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Button size="sm" variant="ghost" className="rounded-full" onClick={() => handleResend(inv)}>
                      <RotateCw className="h-3.5 w-3.5 mr-1.5" /> Resend
                    </Button>
                    <Button size="sm" variant="ghost" className="rounded-full text-destructive hover:text-destructive" onClick={() => handleRevoke(inv.id)}>
                      <Ban className="h-3.5 w-3.5 mr-1.5" /> Revoke
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-2">No pending invitations.</p>
          )}
        </CardContent>
      </Card>

      {/* Stripe Connect */}
      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><CreditCard className="h-4 w-4" /> Stripe Connect</CardTitle>
          <CardDescription>The organization connects its own Stripe account to collect payments.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              {stripeConnected ? (
                <Badge className="rounded-full bg-green-100 text-green-700 hover:bg-green-100 border-0">
                  <span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-green-600 inline-block" /> Connected
                </Badge>
              ) : (
                <Badge variant="secondary" className="rounded-full">
                  <span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-muted-foreground inline-block" /> Not connected
                </Badge>
              )}
              {stripeConnected && (
                <>
                  <Badge variant={org.stripe_connect_charges_enabled ? 'outline' : 'secondary'} className="rounded-full text-xs">
                    Charges {org.stripe_connect_charges_enabled ? 'enabled' : 'pending'}
                  </Badge>
                  <Badge variant={org.stripe_connect_payouts_enabled ? 'outline' : 'secondary'} className="rounded-full text-xs">
                    Payouts {org.stripe_connect_payouts_enabled ? 'enabled' : 'pending'}
                  </Badge>
                </>
              )}
              {org.stripe_connect_account_id && (
                <span className="text-xs text-muted-foreground">{org.stripe_connect_account_id}</span>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {stripeConnected && (
                <Button variant="ghost" size="sm" className="rounded-full" onClick={handleStripeRefresh} disabled={stripeBusy}>
                  <RotateCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
                </Button>
              )}
              <Button variant="outline" className="rounded-full" onClick={handleStripeOnboarding} disabled={stripeBusy}>
                {stripeBusy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ExternalLink className="h-4 w-4 mr-2" />}
                {stripeConnected ? 'Continue onboarding' : 'Start onboarding'}
              </Button>
            </div>
          </div>
          {stripeConnected && (
            <Button variant="link" size="sm" className="px-0 h-auto" onClick={handleStripeDashboard} disabled={stripeBusy}>
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> View Stripe dashboard
            </Button>
          )}
        </CardContent>
      </Card>

      <InviteAdminDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        callerIsSuperAdmin={true}
        defaultOrganizationId={orgId}
        onInvited={() => { refetchInvites(); }}
      />
    </div>
  );
}
